import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostExtraVereinbarungRequest, IPostExtraVereinbarungResponse } from 'fa-kt.types';
import { PostExtraVereinbarungCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-extra-vereinbarung',
  templateUrl: './post-extra-vereinbarung.component.html',
  styleUrls: ['./post-extra-vereinbarung.component.css'],
})
export class PostExtraVereinbarungCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostExtraVereinbarungRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostExtraVereinbarungRequest> = new EventEmitter<IPostExtraVereinbarungRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostExtraVereinbarungResponse> = new EventEmitter<IPostExtraVereinbarungResponse>();

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postExtraVereinbarung: PostExtraVereinbarungCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.FINANZEN.Commands.POST_EXTRA_VEREINBARUNG;
    this.form = this.fb.group({
      extraVereinbarung: this.fb.group({
        id: [null, []],
        kunde: [null, [Validators.required]],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
        pro: [null, [Validators.required]],
        kosten: [null, []],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postExtraVereinbarung.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostExtraVereinbarungRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.extraVereinbarungId?.valueChanges.subscribe(async (id) => {
        /* << on-extra-vereinbarung-id-change    */
        /*    on-extra-vereinbarung-id-change >> */
      }) as Subscription,
      this.extraVereinbarungKunde?.valueChanges.subscribe(async (kunde) => {
        /* << on-extra-vereinbarung-kunde-change    */
        /*    on-extra-vereinbarung-kunde-change >> */
      }) as Subscription,
      this.extraVereinbarungName?.valueChanges.subscribe(async (name) => {
        /* << on-extra-vereinbarung-name-change    */
        /*    on-extra-vereinbarung-name-change >> */
      }) as Subscription,
      this.extraVereinbarungBeschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-extra-vereinbarung-beschreibung-change    */
        /*    on-extra-vereinbarung-beschreibung-change >> */
      }) as Subscription,
      this.extraVereinbarungPro?.valueChanges.subscribe(async (pro) => {
        /* << on-extra-vereinbarung-pro-change    */
        /*    on-extra-vereinbarung-pro-change >> */
      }) as Subscription,
      this.extraVereinbarungKosten?.valueChanges.subscribe(async (kosten) => {
        /* << on-extra-vereinbarung-kosten-change    */
        /*    on-extra-vereinbarung-kosten-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostExtraVereinbarungRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.extraVereinbarung) {
        value.extraVereinbarung = cleanObject(value.extraVereinbarung);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.extraVereinbarung) {
        payload.extraVereinbarung = cleanObject(payload.extraVereinbarung);
      }
      const response: IPostExtraVereinbarungResponse = await this.postExtraVereinbarung.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get extraVereinbarungId() {
    return this.form.controls.extraVereinbarung.get('id');
  }
  get extraVereinbarungKunde() {
    return this.form.controls.extraVereinbarung.get('kunde');
  }
  get extraVereinbarungName() {
    return this.form.controls.extraVereinbarung.get('name');
  }
  get extraVereinbarungBeschreibung() {
    return this.form.controls.extraVereinbarung.get('beschreibung');
  }
  get extraVereinbarungPro() {
    return this.form.controls.extraVereinbarung.get('pro');
  }
  get extraVereinbarungKosten() {
    return this.form.controls.extraVereinbarung.get('kosten');
  }
}
